/**
 * Canonical audit event names. Import `AUDIT_EVENT` at call sites so typos fail at compile time.
 * When adding a new event: add one line here, then use `AUDIT_EVENT.YOUR_KEY` in `recordAuditEvent` / `recordAuditEventBackground`.
 */
export const AUDIT_EVENT = {
  ANNOUNCEMENT_CREATE: "announcement.create",
  ANNOUNCEMENT_DELETE: "announcement.delete",
  ANNOUNCEMENT_LIST_VIEW: "announcement.list.view",
  ANNOUNCEMENT_UPDATE: "announcement.update",
  ADMIN_ACCOUNT_VIEW: "admin.account.view",
  AUDIT_ARCHIVE_PURGE: "audit.archive.purge",
  ADMIN_CLIENT_MANAGER_VIEW: "admin.clientManager.view",
  ASSET_EXPORT: "asset.export",
  ASSET_SEARCH: "asset.search",
  ASSET_SUBDOMAIN_ADD: "asset.subdomain.add",
  ASSET_URL_ADD: "asset.url.add",
  ASSET_VIEW: "asset.view",
  AUTH_MFA_SETUP_SECRET_REVEAL: "auth.mfa.setup.secretReveal",
  AUTH_2FA_RESEND: "auth.2fa.resend",
  AUTH_2FA_VERIFY: "auth.2fa.verify",
  AUTH_LOGIN: "auth.login",
  AUTH_LOGOUT: "auth.logout",
  AUTH_PASSKEY_SIGNIN: "auth.passkey.signin",
  AUTH_PASSWORD_RESET_COMPLETE: "auth.password.reset.complete",
  AUTH_PASSWORD_RESET_REQUEST: "auth.password.reset.request",
  AUTH_SECURITY_STEP_UP_SEND: "auth.security.stepUp.send",
  AUTH_SECURITY_STEP_UP_VERIFY: "auth.security.stepUp.verify",
  AUTH_THROTTLE_LOCKOUT: "auth.throttle.lockout",
  AUTH_THROTTLE_CLEAR: "auth.throttle.clear",
  COGNITO_MIGRATION_SYNC: "cognito.migration.sync",
  COGNITO_MIGRATION_WELCOME_RESEND: "cognito.migration.welcome.resend",
  COGNITO_MIGRATION_TEMP_PASSWORD_REFRESH: "cognito.migration.tempPassword.refresh",
  FINDING_TEMPLATE_CREATE: "findingTemplate.create",
  FINDING_TEMPLATE_DELETE: "findingTemplate.delete",
  FINDING_TEMPLATE_IMPORT: "findingTemplate.import",
  FINDING_TEMPLATE_LIST_VIEW: "findingTemplate.list.view",
  FINDING_TEMPLATE_UPDATE: "findingTemplate.update",
  FINDING_TEMPLATE_VIEW: "findingTemplate.view",
  INVITATION_ADD_TO_ORGANIZATION: "invitation.addToOrganization",
  INVITATION_ADD_TO_WORKSPACE: "invitation.addToWorkspace",
  INVITATION_CREATE: "invitation.create",
  INVITATION_DELETE: "invitation.delete",
  INVITATION_LIST_VIEW: "invitation.list.view",
  INVITATION_RESEND: "invitation.resend",
  LICENSE_CREATE: "license.create",
  LICENSE_DELETE: "license.delete",
  LICENSE_UPDATE: "license.update",
  INTEGRATION_CREATE: "integration.create",
  INTEGRATION_UPDATE: "integration.update",
  INTEGRATION_DELETE: "integration.delete",
  INTEGRATION_TEST: "integration.test",
  LICENSE_VIEW: "license.view",
  LOGS_EXPORT_CSV: "logs.export.csv",
  LOGS_EXPORT_JSONL: "logs.export.jsonl",
  LOGS_PURGE: "logs.purge",
  LOGS_VIEW: "logs.view",
  ONBOARDING_COMPLETE: "onboarding.complete",
  ONBOARDING_OTP: "onboarding.otp",
  ONBOARDING_OTP_RESEND: "onboarding.otp.resend",
  ONBOARDING_PROFILE_SUBMIT: "onboarding.profile.submit",
  ORGANIZATION_CONTACT_SALES: "organization.contactSales",
  ORGANIZATION_CONTACT_SUPPORT: "organization.contactSupport",
  ORGANIZATION_CREATE: "organization.create",
  ORGANIZATION_DELETE: "organization.delete",
  ORGANIZATION_SWITCH: "organization.switch",
  ORGANIZATION_UPDATE: "organization.update",
  OTP_RESEND: "otp.resend",
  OTP_SEND: "otp.send",
  OTP_VERIFY: "otp.verify",
  PASSWORD_CHANGE: "password.change",
  REPORT_CREATE: "report.create",
  REPORT_DELETE: "report.delete",
  REPORT_DOWNLOAD: "report.download",
  REPORT_LIST_VIEW: "report.list.view",
  REPORT_SEND: "report.send",
  REPORT_TRANSLATE: "report.translate",
  REPORT_UPDATE: "report.update",
  S3_SIGNED_URL_GENERATE: "s3.signedUrl.generate",
  S3_SIGNED_URL_REDIRECT: "s3.signedUrl.redirect",
  UPLOAD_PRESIGN: "upload.presign",
  UPLOAD_COMPLETE: "upload.complete",
  UPLOAD_REJECT: "upload.reject",
  SCHEDULE_CREATE: "schedule.create",
  SCAN_CANCEL: "scan.cancel",
  SCAN_DELETE: "scan.delete",
  SCAN_LIST_VIEW: "scan.list.view",
  SCAN_RUN: "scan.run",
  SCAN_SCHEDULE_CREATE: "scan.schedule.create",
  SCAN_SCHEDULE_DELETE: "scan.schedule.delete",
  SCAN_SCHEDULE_LIST_VIEW: "scan.schedule.list.view",
  SCAN_SCHEDULE_UPDATE: "scan.schedule.update",
  /** No longer recorded (the manual status action was removed); kept so older log rows still read well. */
  SCAN_UPDATE_STATUS: "scan.updateStatus",
  /** A scan finished and at least one of its domains succeeded (recorded by the system, once per scan). */
  SCAN_COMPLETED: "scan.completed",
  /** Every domain of a scan failed (recorded by the system, once per scan). */
  SCAN_FAILED: "scan.failed",
  /** A scan summary was delivered (or failed) to one destination: the emails, or one Teams/Slack integration. */
  SCAN_SUMMARY_SENT: "scan.summary.sent",
  SETTINGS_AUTHENTICATOR_REMOVE: "settings.authenticator.remove",
  SETTINGS_AUTHENTICATOR_SETUP_COMPLETE: "settings.authenticator.setup.complete",
  SETTINGS_AUTHENTICATOR_SETUP_START: "settings.authenticator.setup.start",
  SETTINGS_PASSKEY_DELETE: "settings.passkey.delete",
  SETTINGS_PASSKEY_REGISTER: "settings.passkey.register",
  SETTINGS_UPDATE: "settings.update",
  SUPPLIER_CREATE: "supplier.create",
  SUPPLIER_CREATE_AND_RUN_SCAN: "supplier.createAndRunScan",
  SUPPLIER_DELETE: "supplier.delete",
  SUPPLIER_LIST_VIEW: "supplier.list.view",
  SUPPLIER_REPORT_SEND: "supplier.report.send",
  SUPPLIER_RUN_SCAN: "supplier.runScan",
  SUPPLIER_SCAN_DELETE: "supplier.scan.delete",
  SUPPLIER_SCHEDULE_CREATE: "supplier.schedule.create",
  SUPPLIER_SCHEDULE_DELETE: "supplier.schedule.delete",
  SUPPLIER_SCHEDULE_LIST_VIEW: "supplier.schedule.list.view",
  SUPPLIER_SCHEDULE_UPDATE: "supplier.schedule.update",
  SUPPLIER_UPDATE: "supplier.update",
  SUPPLIER_VIEW: "supplier.view",
  USER_DELETE: "user.delete",
  USER_LOGIN_DISABLED_UPDATE: "user.loginDisabled.update",
  USER_MANAGEMENT_VIEW: "user.management.view",
  USER_ADMIN_LIST_VIEW: "user.admin.list.view",
  USER_LIST_VIEW: "user.list.view",
  USER_ORGANIZATION_ASSIGN: "user.organization.assign",
  USER_ORGANIZATION_UNASSIGN: "user.organization.unassign",
  USER_ORGANIZATION_ROLE_UPDATE: "user.organizationRole.update",
  USER_PROFILE_UPDATE: "user.profile.update",
  USER_SUPER_USER_UPDATE: "user.superUser.update",
  USER_WORKSPACE_ASSIGN: "user.workspace.assign",
  USER_WORKSPACE_UNASSIGN: "user.workspace.unassign",
  USER_WORKSPACE_ROLE_UPDATE: "user.workspaceRole.update",
  USER_ASSIGNMENT_SCOPE_UPDATE: "user.assignmentScope.update",
  VULNERABILITY_AI_ENHANCE: "vulnerability.ai.enhance",
  VULNERABILITY_ATTACHMENT_REMOVE: "vulnerability.attachment.remove",
  VULNERABILITY_ATTACHMENT_UPLOAD: "vulnerability.attachment.upload",
  VULNERABILITY_CREATE: "vulnerability.create",
  VULNERABILITY_DELETE: "vulnerability.delete",
  VULNERABILITY_LIST_VIEW: "vulnerability.list.view",
  VULNERABILITY_REPORT_SEND: "vulnerability.report.send",
  VULNERABILITY_STATUS_UPDATE: "vulnerability.status.update",
  VULNERABILITY_UPDATE: "vulnerability.update",
  VULNERABILITY_VIEW: "vulnerability.view",
  WORKSPACE_CREATE: "workspace.create",
  WORKSPACE_DELETE: "workspace.delete",
  WORKSPACE_DOMAIN_DELETE: "workspace.domain.delete",
  WORKSPACE_LIST_VIEW: "workspace.list.view",
  WORKSPACE_SWITCH: "workspace.switch",
  WORKSPACE_UPDATE: "workspace.update",
  INFOSTEALERS_IMPORT: "infostealers.import",
  INFOSTEALER_MONITOR_UPDATE: "infostealer.monitor.update",
  INFOSTEALER_MONITOR_DELETE: "infostealer.monitor.delete",
  INFOSTEALER_MONITOR_RUN: "infostealer.monitor.run",
  CHAT_MESSAGE: "chat.message",
  CC_DASHBOARD_VIEW: "cc_dashboard.view",
  REPORT_GENERATE_ORG: "report.generate_org",
} as const;

export type AuditEventName = (typeof AUDIT_EVENT)[keyof typeof AUDIT_EVENT];
